import React, { useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

import { clearToken } from '../utilities/auth';
import { authFetch } from '../services/authFetch';
import { msalInstance } from '../msalConfig';
import { prepViewsForDisplay } from '../utilities/views';
import { logOut } from '../redux/authSlice';
import { setActiveView } from '../redux/viewSlice';
import TopbarCategory from './TopbarCategory';
import { getMenuItems } from '../api';
import { UserType } from '../constants'

import logoCroppedImage from '../images/sla-logo-cropped.png';
import closeIcon from '../images/close.svg';
import hamburgerMenuIcon from '../images/hamburger-menu.svg';
import logOutIcon from '../images/logout.svg';
import leftArrowIcon from '../images/left-arrow.svg';
import './Topbar.scss';

function Topbar(props) {
    const dispatch = useDispatch();

    const activeView = useSelector(state => state.view.activeView);
    const userType = useSelector(state => state.auth.userType);

    const [isExpanded, setIsExpanded] = useState(false);
    const [openCategory, setOpenCategory] = useState(null);
    const [views, setViews] = useState({});
    const [viewError, setViewError] = useState(null);
    const [tableauAccountDetails, setTableauAccountDetails] = useState({});
    const [address, setAddress] = useState({});

    const handleLogOut = useCallback(() => {
        clearToken();

        dispatch(logOut());

        if (msalInstance.getAllAccounts().length > 0) {
            msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
        }
    }, [dispatch]);

    const setActiveViewWrapper = (view) => {
        setOpenCategory(null);
        setIsExpanded(false);

        dispatch(setActiveView(view));
    };

    useEffect(() => {
        if (!isExpanded) {
            setOpenCategory(null);
        }
    }, [isExpanded]);

    useEffect(() => {
        setViewError(null);
        
        getMenuItems({ setViewError, setViews, handleLogOut, setAddress });
    }, [handleLogOut]);

    useEffect(() => {
        setTableauAccountDetails({});

        authFetch(`${window.AnalyticsPortalConfig.apiAddress}/TableauSessions/AccountDetails`)
            .then(result => {
                if (!result.ok) {
                    if (result.status === 401) {
                        throw new Error('401');
                    }

                    throw new Error('Response not OK.');
                }
                
                return result.json();
            })
            .then((result) => {
                setTableauAccountDetails(result);
            })
            .catch((error) => {
                if (error.message === '401') {
                    handleLogOut();

                    return;
                }

                console.error(error);
            });
    }, [handleLogOut]);

    let userBannerTitle = null;

    if (Object.keys(tableauAccountDetails).length > 0 && tableauAccountDetails.isGenericUser) {
        userBannerTitle = "If you believe you should have a non-generic Tableau account, please contact the IT department.";
    }

    const showFooter = isExpanded && openCategory === null;

    return (
        <nav id="topbar" className={clsx("d-flex flex-column w-100",
            isExpanded && "expanded")}>
            <div id="topbar-static" className="d-flex align-items-center pl-4 pr-2">
                <div>
                    {openCategory && 
                        <button
                            id="topbar-collapse-button"
                            className="btn link"
                            onClick={() => setOpenCategory(null)}>
                            <img src={leftArrowIcon} alt="Close Category" />
                        </button>
                    }
                    {!openCategory &&
                        <button
                            className="btn"
                            onClick={() => setActiveViewWrapper(null)}>
                            <img
                                id="topbar-logo"
                                className="ml-3"
                                src={logoCroppedImage}
                                alt="SLA Analytics Logo" />
                        </button>
                    }
                </div>
                <button
                    id="topbar-open-close"
                    className="btn link ml-auto"
                    onClick={() => setIsExpanded(!isExpanded)}>
                    <img    
                        src={isExpanded ? closeIcon : hamburgerMenuIcon}
                        alt={isExpanded ? "Close Menu" : "Open Menu"}
                        className="svg-image" />
                </button>
            </div>
            <div className={clsx("category-container",
                !isExpanded && "d-none")}>
                {viewError !== null && 
                    <p className="topbar-message text-center pt-5">{viewError}</p>
                }
                {viewError === null && Object.keys(views).length === 0 &&
                    <p className="topbar-message text-center pt-5">Loading Views...</p>
                }
                {viewError === null && Object.keys(views).length > 0 && prepViewsForDisplay(views).map(([key, value]) =>
                    <TopbarCategory
                        key={key}
                        name={key}
                        isOpen={openCategory === key}
                        open={() => setOpenCategory(key)}
                        views={value}
                        activeView={activeView}
                        setActiveView={setActiveViewWrapper} />
                )}
            </div>
            <div id="topbar-footer" className={clsx("mt-auto py-4 flex-column align-items-center", 
                !showFooter && "d-none",
                showFooter && "d-flex")}>
                {Object.keys(tableauAccountDetails).length > 0 &&
                    <div title={userBannerTitle} className="user-banner">
                        Welcome, {tableauAccountDetails.fullName}!
                    </div>
                }
                {address.rapid && userType === UserType.SLAUser &&
                    <a href={address.rapid}
                        className="btn link w-100"
                        target="_blank"
                        rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> RAPID</a>
                }
                {address.SLIP && userType === UserType.SLIPUser &&
                    <a href={address.SLIP}
                        className="btn link w-100 d-flex align-items-center"
                        target="_blank"
                        rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> SLIP</a>
                }
                {address.learningCenter && userType === UserType.SLIPUser &&
                    <a href={address.learningCenter}
                        className="btn link w-100 d-flex align-items-center"
                        target="_blank"
                        rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> Learning Center</a>
                }
                {address.slacalWebsite && userType === UserType.SLIPUser &&
                    <a href={address.slacalWebsite}
                        className="btn link w-100 d-flex align-items-center"
                        target="_blank"
                        rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> Website</a>
                }
                <a href="https://www.slacal.com/send-us-a-message"
                    className="btn link w-100 d-flex align-items-center"
                    target="_blank"
                    rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> Contact Us</a>
                <button className="btn btn-block link" onClick={handleLogOut}>
                    <img src={logOutIcon} alt="Log Out" className="svg-image mr-2" /> Log Out
                </button>
            </div>
        </nav>
    );
}

export default Topbar;